import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const ShopifyContext = createContext(null);

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within ShopifyAppBridgeProvider');
  }
  return context;
};

/**
 * ShopifyAppBridgeProvider
 * 
 * Uses the Shopify App Bridge CDN script (loaded in index.html) to provide
 * session tokens for embedded app authentication.
 * 
 * When embedded in Shopify admin:
 * - window.shopify is available (from CDN)
 * - shopify.idToken() provides a session token (JWT, 1-min lifetime)
 * - Token is sent as Authorization header on every API request
 * 
 * When standalone (direct URL access):
 * - Falls back to email/password JWT auth
 */
export const ShopifyAppBridgeProvider = ({ children }) => {
  const [searchParams] = useSearchParams();

  const shopifyParams = useMemo(() => {
    const shop = searchParams.get('shop');
    const host = searchParams.get('host');
    const orderId = searchParams.get('id') || searchParams.get('order_id');
    const locale = searchParams.get('locale') || 'en';

    // Embedded if App Bridge CDN loaded AND we have shop+host params
    const isEmbedded = Boolean(window.shopify && shop && host);
    const isStandalone = !isEmbedded;

    return { shop, host, orderId, locale, isEmbedded, isStandalone };
  }, [searchParams]);

  const [sessionReady, setSessionReady] = useState(false);

  // Initialize App Bridge when embedded
  useEffect(() => {
    if (shopifyParams.isEmbedded && window.shopify) {
      // App Bridge CDN auto-initializes when loaded in Shopify admin iframe
      setSessionReady(true);
    }
  }, [shopifyParams.isEmbedded]);

  // Get a fresh session token (1-min lifetime, fetch on each request)
  const getSessionToken = useCallback(async () => {
    if (!shopifyParams.isEmbedded || !window.shopify) {
      return null;
    }
    try {
      const token = await window.shopify.idToken();
      return token;
    } catch (err) {
      console.error('Failed to get Shopify session token:', err);
      return null;
    }
  }, [shopifyParams.isEmbedded]);

  const contextValue = useMemo(() => ({
    ...shopifyParams,
    sessionReady,
    getSessionToken,
    buildUrl: (path) => {
      const params = new URLSearchParams();
      if (shopifyParams.shop) params.set('shop', shopifyParams.shop);
      if (shopifyParams.host) params.set('host', shopifyParams.host);
      const qs = params.toString();
      return qs ? `${path}?${qs}` : path;
    },
  }), [shopifyParams, sessionReady, getSessionToken]);

  return (
    <ShopifyContext.Provider value={contextValue}>
      {children}
    </ShopifyContext.Provider>
  );
};

export default ShopifyAppBridgeProvider;
